import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAction, useGlobalAction } from '@gadgetinc/react';
import {
  Box,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  Textarea,
  Select,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Switch,
  Button,
  Card,
  CardHeader,
  CardBody,
  Heading,
  Text,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Spinner,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  CheckboxGroup,
  Checkbox,
  Divider,
  useToast,
  Container,
  SimpleGrid,
  Badge,
  Tooltip,
} from '@chakra-ui/react';
import { api } from '../api';

export default function CreateAgent() {
  const navigate = useNavigate();
  const toast = useToast();

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    instructions: '',
    model: 'gpt-4o-mini',
    temperature: 1,
    topP: 1,
    maxTokens: 4096,
    fileSearchEnabled: false,
    codeInterpreterEnabled: false,
    webBrowsingEnabled: false,
    selectedVectorStores: [],
    backgroundProcessingEnabled: false,
    processingTimeout: 300,
    processingMode: 'async'
  });

  const [formErrors, setFormErrors] = useState({});
  const [vectorStores, setVectorStores] = useState([]);

  // Action hooks
  const [{ data: createResult, fetching: creating, error: createError }, createAgent] = useGlobalAction(api.createAgentOnVM);
  const [{ data: vectorStoreData, fetching: loadingVectorStores, error: vectorStoreError }, getUserVectorStores] = useGlobalAction(api.getUserVectorStores);

  // Load vector stores on component mount
  useEffect(() => {
    getUserVectorStores();
  }, []);

  // Update vector stores when data is loaded
  useEffect(() => {
    if (vectorStoreData && !loadingVectorStores) {
      // Add logging to debug the data structure
      console.log('Vector store data received:', vectorStoreData);
      console.log('Type of vectorStoreData:', typeof vectorStoreData);
      console.log('Is array:', Array.isArray(vectorStoreData));
      
      // Handle both array and object formats
      let stores = [];
      if (Array.isArray(vectorStoreData)) {
        // Direct array format
        stores = vectorStoreData;
      } else if (vectorStoreData && vectorStoreData.vectorStores) {
        // Object with vectorStores property
        stores = vectorStoreData.vectorStores;
      } else if (vectorStoreData && typeof vectorStoreData === 'object') {
        // Single object, wrap in array
        stores = [vectorStoreData];
      }
      
      console.log('Setting vector stores:', stores);
      setVectorStores(stores || []);
    }
  }, [vectorStoreData, loadingVectorStores]);

  // Handle form field changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear field error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  // Handle vector store selection
  const handleVectorStoreChange = (selectedValues) => {
    handleInputChange('selectedVectorStores', selectedValues);
  };



  // Form validation
  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Agent name is required';
    }
    
    if (!formData.instructions.trim()) {
      errors.instructions = 'Instructions are required';
    }
    
    if (formData.fileSearchEnabled && formData.selectedVectorStores.length === 0) {
      errors.selectedVectorStores = 'Please select at least one vector store when file search is enabled';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast({
        title: 'Validation Error',
        description: 'Please fix the errors in the form before submitting.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      // Prepare the data structure expected by createAgentOnVM
      const agentData = {
        name: formData.name,
        description: formData.description,
        instructions: formData.instructions,
        modelSettings: {
          model: formData.model,
          temperature: formData.temperature,
          top_p: formData.topP,
          max_tokens: formData.maxTokens
        },
        tools: {
          fileSearch: {
            enabled: formData.fileSearchEnabled
          },
          codeInterpreter: {
            enabled: formData.codeInterpreterEnabled
          },
          webBrowsing: {
            enabled: formData.webBrowsingEnabled
          }
        },
        backgroundProcessing: {
          enabled: formData.backgroundProcessingEnabled,
          timeoutMs: formData.processingTimeout * 1000, // Convert seconds to milliseconds
          mode: formData.processingMode
        },
        vectorStoreIds: formData.fileSearchEnabled ? formData.selectedVectorStores : []
      };

      await createAgent(agentData);
      
    } catch (error) {
      console.error('Error creating agent:', error);
    }
  };

  // Handle successful creation
  useEffect(() => {
    if (createResult && !creating) {
      toast({
        title: 'Agent Created Successfully!',
        description: `Agent "${formData.name}" has been created and is ready to use.`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      
      // Redirect to agent chat after a short delay
      setTimeout(() => {
        navigate(`/agents/vm/${createResult.agent?.id || createResult.id || createResult.agentId}`);
      }, 1000);
    }
  }, [createResult, creating, formData.name, navigate, toast]);

  return (
    <Container maxW="container.lg" py={8}>
      <VStack spacing={8} align="stretch">
        <Box>
          <Heading size="lg" mb={2}>Create New Agent</Heading>
          <Text color="gray.600">
            Configure your AI agent with custom instructions, model settings, and capabilities.
          </Text>
        </Box>

        {createError && (
          <Alert status="error">
            <AlertIcon />
            <AlertTitle>Error creating agent:</AlertTitle>
            <AlertDescription>{createError.message}</AlertDescription>
          </Alert>
        )}

        {vectorStoreError && (
          <Alert status="error">
            <AlertIcon />
            <AlertTitle>Error loading vector stores:</AlertTitle>
            <AlertDescription>{vectorStoreError.message}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <VStack spacing={6} align="stretch">
            {/* Basic Agent Info */}
            <Card>
              <CardHeader>
                <Heading size="md">Basic Information</Heading>
              </CardHeader>
              <CardBody>
                <VStack spacing={4} align="stretch">
                  <FormControl isRequired isInvalid={formErrors.name}>
                    <FormLabel>Agent Name</FormLabel>
                    <Input
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      placeholder="Enter a unique name for your agent"
                    />
                    {formErrors.name && (
                      <Text color="red.500" fontSize="sm">{formErrors.name}</Text>
                    )}
                  </FormControl>

                  <FormControl>
                    <FormLabel>Description</FormLabel>
                    <Input
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      placeholder="Brief description of what this agent does"
                    />
                    <FormHelperText>
                      Optional description to help identify this agent's purpose
                    </FormHelperText>
                  </FormControl>

                  <FormControl isRequired isInvalid={formErrors.instructions}>
                    <FormLabel>Instructions</FormLabel>
                    <Textarea
                      value={formData.instructions}
                      onChange={(e) => handleInputChange('instructions', e.target.value)}
                      placeholder="Provide detailed instructions for how the agent should behave..."
                      minH="120px"
                      resize="vertical"
                    />
                    <FormHelperText>
                      Detailed instructions that define the agent's personality, capabilities, and behavior
                    </FormHelperText>
                    {formErrors.instructions && (
                      <Text color="red.500" fontSize="sm">{formErrors.instructions}</Text>
                    )}
                  </FormControl>
                </VStack>
              </CardBody>
            </Card>

            {/* Model Settings */}
            <Card>
              <CardHeader>
                <Heading size="md">Model Settings</Heading>
              </CardHeader>
              <CardBody>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl>
                    <FormLabel>Model</FormLabel>
                    <Select
                      value={formData.model}
                      onChange={(e) => handleInputChange('model', e.target.value)}
                    >
                      <option value="gpt-4o">GPT-4O (Most Capable)</option>
                      <option value="gpt-4o-mini">GPT-4O Mini (Fast & Efficient)</option>
                      <option value="gpt-4-turbo">GPT-4 Turbo (Legacy)</option>
                    </Select>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Max Tokens</FormLabel>
                    <NumberInput
                      value={formData.maxTokens}
                      onChange={(valueString) => handleInputChange('maxTokens', parseInt(valueString) || 4096)}
                      min={1}
                      max={32768}
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                    <FormHelperText>Maximum length of the agent's response</FormHelperText>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Temperature: {formData.temperature}</FormLabel>
                    <Slider
                      value={formData.temperature}
                      onChange={(value) => handleInputChange('temperature', value)}
                      min={0}
                      max={2}
                      step={0.1}
                    >
                      <SliderTrack>
                        <SliderFilledTrack />
                      </SliderTrack>
                      <SliderThumb />
                    </Slider>
                    <FormHelperText>
                      Controls randomness: 0 = focused, 2 = creative
                    </FormHelperText>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Top P: {formData.topP}</FormLabel>
                    <Slider
                      value={formData.topP}
                      onChange={(value) => handleInputChange('topP', value)}
                      min={0}
                      max={1}
                      step={0.05}
                    >
                      <SliderTrack>
                        <SliderFilledTrack />
                      </SliderTrack>
                      <SliderThumb />
                    </Slider>
                    <FormHelperText>
                      Controls diversity: lower values = more focused
                    </FormHelperText>
                  </FormControl>
                </SimpleGrid>
              </CardBody>
            </Card>

            {/* Tools Configuration */}
            <Card>
              <CardHeader>
                <Heading size="md">Tools & Capabilities</Heading>
              </CardHeader>
              <CardBody>
                <VStack spacing={4} align="stretch">
                  <HStack justify="space-between">
                    <Box>
                      <Text fontWeight="medium">File Search</Text>
                      <Text fontSize="sm" color="gray.600">
                        Enable the agent to search through uploaded documents using vector stores
                      </Text>
                    </Box>
                    <Switch
                      isChecked={formData.fileSearchEnabled}
                      onChange={(e) => handleInputChange('fileSearchEnabled', e.target.checked)}
                    />
                  </HStack>

                  {formData.fileSearchEnabled && (
                    <Box pl={4} borderLeft="2px" borderColor="blue.200">
                      <FormControl isInvalid={formErrors.selectedVectorStores}>
                        <FormLabel>Select Vector Stores</FormLabel>
                        {loadingVectorStores ? (
                          <HStack>
                            <Spinner size="sm" />
                            <Text>Loading vector stores...</Text>
                          </HStack>
                        ) : vectorStores.length > 0 ? (
                          <CheckboxGroup
                            value={formData.selectedVectorStores}
                            onChange={handleVectorStoreChange}
                          >
                            <VStack align="start" spacing={3}>
                              {vectorStores.map((store) => (
                                <Checkbox key={store.id} value={store.id}>
                                  <VStack align="start" spacing={1}>
                                    <Text fontWeight="medium">{store.name}</Text>
                                    {store.description && (
                                      <Text fontSize="sm" color="gray.600">
                                        {store.description}
                                      </Text>
                                    )}
                                    <HStack>
                                      <Badge colorScheme={store.status === 'completed' ? 'green' : 'yellow'} size="sm">
                                        {store.status}
                                      </Badge>
                                      {store.fileCount && (
                                        <Badge colorScheme="blue" size="sm">
                                          {store.fileCount} files
                                        </Badge>
                                      )}
                                    </HStack>
                                  </VStack>
                                </Checkbox>
                              ))}
                            </VStack>
                          </CheckboxGroup>
                        ) : (
                          <Text color="gray.500">
                            No vector stores available. Please create some vector stores first.
                          </Text>
                        )}
                        <FormHelperText>
                          Select the vector stores you want the agent to search through.
                        </FormHelperText>
                        {formErrors.selectedVectorStores && (
                          <Text color="red.500" fontSize="sm">{formErrors.selectedVectorStores}</Text>
                        )}
                      </FormControl>
                    </Box>
                  )}

                  <Divider />

                  <HStack justify="space-between">
                    <Box>
                      <Text fontWeight="medium">Code Interpreter</Text>
                      <Text fontSize="sm" color="gray.600">
                        Allow the agent to run Python code and analyze data
                      </Text>
                    </Box>
                    <Switch
                      isChecked={formData.codeInterpreterEnabled}
                      onChange={(e) => handleInputChange('codeInterpreterEnabled', e.target.checked)}
                    />
                  </HStack>

                  <Divider />

                  <HStack justify="space-between">
                    <Box>
                      <Text fontWeight="medium">Web Browsing</Text>
                      <Text fontSize="sm" color="gray.600">
                        Enable web browsing capabilities (coming soon)
                      </Text>
                    </Box>
                    <Tooltip label="Coming soon" placement="left">
                      <Switch
                        isChecked={formData.webBrowsingEnabled}
                        onChange={(e) => handleInputChange('webBrowsingEnabled', e.target.checked)}
                        isDisabled={true}
                      />
                    </Tooltip>
                  </HStack>
                </VStack>
              </CardBody>
            </Card>

            {/* Background Processing Options */}
            <Card>
              <CardHeader>
                <Heading size="md">Background Processing</Heading>
              </CardHeader>
              <CardBody>
                <VStack spacing={4} align="stretch">
                  <HStack justify="space-between">
                    <Box>
                      <Text fontWeight="medium">Enable Background Processing</Text>
                      <Text fontSize="sm" color="gray.600">
                        Allow the agent to handle long-running tasks in the background
                      </Text>
                    </Box>
                    <Switch
                      isChecked={formData.backgroundProcessingEnabled}
                      onChange={(e) => handleInputChange('backgroundProcessingEnabled', e.target.checked)}
                    />
                  </HStack>

                  {formData.backgroundProcessingEnabled && (
                    <Box pl={4} borderLeft="2px" borderColor="blue.200">
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                        <FormControl>
                          <FormLabel>Processing Timeout (seconds)</FormLabel>
                          <NumberInput
                            value={formData.processingTimeout}
                            onChange={(valueString) => handleInputChange('processingTimeout', parseInt(valueString) || 300)}
                            min={30}
                            max={3600}
                          >
                            <NumberInputField />
                            <NumberInputStepper>
                              <NumberIncrementStepper />
                              <NumberDecrementStepper />
                            </NumberInputStepper>
                          </NumberInput>
                          <FormHelperText>Maximum time for background tasks</FormHelperText>
                        </FormControl>

                        <FormControl>
                          <FormLabel>Processing Mode</FormLabel>
                          <Select
                            value={formData.processingMode}
                            onChange={(e) => handleInputChange('processingMode', e.target.value)}
                          >
                            <option value="async">Asynchronous</option>
                            <option value="sync">Synchronous</option>
                            <option value="queue">Queue-based</option>
                          </Select>
                          <FormHelperText>How background tasks are handled</FormHelperText>
                        </FormControl>
                      </SimpleGrid>
                    </Box>
                  )}
                </VStack>
              </CardBody>
            </Card>

            {/* Submit Button */}
            <Card>
              <CardBody>
                <VStack spacing={4}>
                  <Button
                    type="submit"
                    colorScheme="blue"
                    size="lg"
                    width={{ base: "100%", md: "auto" }}
                    px={8}
                    isLoading={creating}
                    loadingText="Creating Agent..."
                  >
                    Create Agent
                  </Button>
                  
                  {creating && (
                    <HStack>
                      <Spinner size="sm" />
                      <Text color="gray.600">
                        Creating your agent on the virtual machine...
                      </Text>
                    </HStack>
                  )}
                </VStack>
              </CardBody>
            </Card>
          </VStack>
        </form>
      </VStack>
    </Container>
  );
}